import { useCurrencyStore } from '@/stores/currency';
import type { Product } from '@/types';

const toNumber = (value: number | string | undefined | null): number | undefined => {
  if (value === null || value === undefined) return undefined;

  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatUSD = (amount: number): string => {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })}`;
};

const formatPYG = (amount: number): string => {
  return `Gs. ${Math.round(amount).toLocaleString('es-PY', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const applyDiscountRule = (
  basePrice: number,
  adjustmentType?: string | null,
  adjustmentMode?: string | null,
  adjustmentValue?: number,
): number | undefined => {
  if (!Number.isFinite(basePrice) || basePrice <= 0 || !Number.isFinite(adjustmentValue) || !adjustmentValue || adjustmentValue <= 0) {
    return undefined;
  }

  const normalizedType = String(adjustmentType || '').toLowerCase();
  const normalizedMode = String(adjustmentMode || '').toLowerCase();

  if (normalizedType === 'discount' || normalizedType === 'descuento') {
    if (normalizedMode === 'percentage' || normalizedMode === 'porcentaje') {
      return Number((basePrice * (1 - adjustmentValue / 100)).toFixed(4));
    }

    if (normalizedMode === 'fixed' || normalizedMode === 'fijo') {
      return Number((basePrice - adjustmentValue).toFixed(4));
    }
  }

  return undefined;
};

const normalizeCurrencyCode = (currency?: string | null): 'PYG' | 'USD' | undefined => {
  const normalized = String(currency || '').trim().toUpperCase();

  if (['PYG', 'GS', 'GS.', 'GUARANI', 'GUARANIES', 'GUARANÍ', 'GUARANÍES'].includes(normalized)) {
    return 'PYG';
  }

  if (['USD', 'US$', '$', 'DOLAR', 'DÓLAR', 'DOLARES', 'DÓLARES'].includes(normalized)) {
    return 'USD';
  }

  return undefined;
};

const resolveDisplayAmount = (requestedPrice: number, product: Product, selectedCurrency: 'PYG' | 'USD'): number => {
  const backendCurrency = normalizeCurrencyCode(product.currency_code);
  const originalCurrency = normalizeCurrencyCode(product.original_currency_code);
  const exchangeRate = product.exchange_rate_sell || product.exchange_rate_buy || 0;

  // El precio principal recibido del backend ya está expresado en
  // `currency_code`. Si coincide con la moneda elegida, debe conservarse
  // exactamente y no volver a aplicarse ninguna cotización.
  if (backendCurrency === selectedCurrency) {
    return requestedPrice;
  }

  const effectiveOfferPrice = getEffectiveOfferPrice(product);
  const isOfferPrice = effectiveOfferPrice !== undefined
    ? Math.abs(requestedPrice - effectiveOfferPrice) < 0.0001
    : product.offer_price !== undefined && requestedPrice === product.offer_price;
  const backendAmount = requestedPrice;
  const originalAmount = isOfferPrice
    ? getEffectiveOriginalOfferPrice(product)
    : toNumber(product.original_price);

  if (selectedCurrency === 'USD') {
    if (originalCurrency === 'USD' && originalAmount !== undefined) {
      return originalAmount;
    }

    if (backendCurrency === 'USD') {
      return backendAmount;
    }

    if (backendCurrency === 'PYG' && exchangeRate > 0) {
      return backendAmount / exchangeRate;
    }
  }

  if (selectedCurrency === 'PYG') {
    if (backendCurrency === 'PYG') {
      return backendAmount;
    }

    if (originalCurrency === 'PYG' && originalAmount !== undefined) {
      return originalAmount;
    }

    if (backendCurrency === 'USD' && exchangeRate > 0) {
      return backendAmount * exchangeRate;
    }

    if (originalCurrency === 'USD' && originalAmount !== undefined && exchangeRate > 0) {
      return originalAmount * exchangeRate;
    }
  }

  return backendAmount;
};

export const getDisplayAmountForCurrency = (
  amount: number | string,
  product: Product,
  targetCurrency: 'PYG' | 'USD',
): number => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (!Number.isFinite(numericAmount)) {
    return 0;
  }

  return resolveDisplayAmount(numericAmount, product, targetCurrency);
};

const isWithinActiveRange = (startDate?: string | null, endDate?: string | null): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
  const end = endDate ? new Date(`${endDate}T23:59:59`) : null;

  if (start && today < start) return false;
  if (end && today > end) return false;

  return true;
};

export const getEffectiveOfferPrice = (product?: Product | null): number | undefined => {
  if (!product) return undefined;

  if (product.offer_price && product.offer_price < product.price) {
    return product.offer_price;
  }

  const activeListPrice = product.product_prices?.find(productPrice => {
    const priceList = productPrice.price_list;

    if (productPrice.price_type !== 'list' || priceList?.is_active === false) {
      return false;
    }

    return isWithinActiveRange(priceList?.valid_from, priceList?.valid_to)
      && isWithinActiveRange(productPrice.start_date, productPrice.end_date);
  });

  if (!activeListPrice) {
    return undefined;
  }

  const directListPrice = Number(activeListPrice.price || 0);

  if (Number.isFinite(directListPrice) && directListPrice > 0 && directListPrice < product.price) {
    return Number(directListPrice.toFixed(4));
  }

  if (!activeListPrice.price_list?.adjustment_type || !activeListPrice.price_list.adjustment_value) {
    return undefined;
  }

  const basePrice = Number(activeListPrice.price || product.price);
  const adjustmentValue = Number(activeListPrice.price_list.adjustment_value);
  return applyDiscountRule(
    basePrice,
    activeListPrice.price_list.adjustment_type,
    activeListPrice.price_list.type,
    adjustmentValue,
  );
};

const getEffectiveOriginalOfferPrice = (product?: Product | null): number | undefined => {
  if (!product) return undefined;

  const originalPrice = toNumber(product.original_price);
  if (!originalPrice || originalPrice <= 0) {
    return undefined;
  }

  const originalOfferPrice = toNumber(product.original_offer_price);
  if (originalOfferPrice && originalOfferPrice > 0 && originalOfferPrice < originalPrice) {
    return originalOfferPrice;
  }

  const activeListPrice = product.product_prices?.find(productPrice => {
    const priceList = productPrice.price_list;

    if (productPrice.price_type !== 'list' || priceList?.is_active === false) {
      return false;
    }

    return isWithinActiveRange(priceList?.valid_from, priceList?.valid_to)
      && isWithinActiveRange(productPrice.start_date, productPrice.end_date);
  });

  const ruleBasedOffer = applyDiscountRule(
    originalPrice,
    activeListPrice?.price_list?.adjustment_type,
    activeListPrice?.price_list?.type,
    Number(activeListPrice?.price_list?.adjustment_value),
  );

  if (ruleBasedOffer && ruleBasedOffer > 0 && ruleBasedOffer < originalPrice) {
    return ruleBasedOffer;
  }

  const effectiveOfferPrice = getEffectiveOfferPrice(product);
  if (effectiveOfferPrice && product.price > 0 && effectiveOfferPrice < product.price) {
    return Number((originalPrice * (effectiveOfferPrice / product.price)).toFixed(4));
  }

  return undefined;
};

export const hasEffectiveDiscount = (product?: Product | null): boolean => {
  const offerPrice = getEffectiveOfferPrice(product);
  return Boolean(product && offerPrice && offerPrice < product.price);
};

export const getEffectiveDisplayPrice = (product?: Product | null): number => {
  const offerPrice = getEffectiveOfferPrice(product);
  return offerPrice !== undefined ? offerPrice : Number(product?.price || 0);
};

export const getEffectiveDisplayPriceForCurrency = (
  product?: Product | null,
  targetCurrency: 'PYG' | 'USD' = 'PYG',
): number => {
  if (!product) return 0;

  return getDisplayAmountForCurrency(getEffectiveDisplayPrice(product), product, targetCurrency);
};

export const getDiscountPercentage = (product?: Product | null): number => {
  const offerPrice = getEffectiveOfferPrice(product);

  if (!product || !offerPrice || offerPrice >= product.price) {
    return 0;
  }

  return Math.round(((product.price - offerPrice) / product.price) * 100);
};

// Legacy function for backward compatibility
export const formatCurrency = (amount: number): string => {
  // Convertir a número si viene como string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Formatear con punto como separador de miles (formato paraguayo)
  return `Gs. ${numericAmount.toLocaleString('es-PY', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true
  })}`;
};

/**
 * Format product price based on selected currency
 * @param price - The price in the product's original currency (from backend)
 * @param product - Optional product object with currency info from backend
 * @returns Formatted price string
 */
export const formatPrice = (price: number | string, product?: Product): string => {
  try {
    const currencyStore = useCurrencyStore();
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Check if price is valid
    if (isNaN(numericPrice) || numericPrice === null || numericPrice === undefined) {
      return 'Gs. 0';
    }
    
    const selectedCurrency = currencyStore.currentCurrency;

    // If product has backend currency data, use it
    if (product && (product.currency_code || product.original_currency_code)) {
      const displayAmount = resolveDisplayAmount(numericPrice, product, selectedCurrency);
      return selectedCurrency === 'USD' ? formatUSD(displayAmount) : formatPYG(displayAmount);
    }

    // Fallback: Use currency store's default formatting
    // This handles products without currency data (old cart items)
    return currencyStore.formatPrice(numericPrice);
  } catch (error) {
    // Final fallback to legacy function if store is not available
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) {
      return 'Gs. 0';
    }
    return formatPriceLegacy(price);
  }
};

// Legacy function renamed for backward compatibility
export const formatPriceLegacy = (price: number | string): string => {
  // Convertir a número si viene como string
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // En Paraguay no se multiplica por 1000, usar el valor directo de la API
  return formatCurrency(numericPrice);
};
